import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Restaurant = Database['public']['Tables']['restaurants']['Row'];

interface RestaurantContextType {
  restaurants: Restaurant[];
  selectedRestaurant: Restaurant | null;
  setSelectedRestaurant: (restaurant: Restaurant | null) => void;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<Restaurant[]>;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

const STORAGE_KEY = 'miam_selected_restaurant_id';

// Helper to sync subscription pricing after restaurant count changes
const syncSubscriptionPricing = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('update-subscription-pricing');
    if (error) {
      console.log('[RestaurantContext] Subscription sync skipped:', error.message);
      return;
    }
    if (data?.success && data?.updated) {
      console.log('[RestaurantContext] Subscription updated:', data.message);
    }
  } catch (err) {
    console.log('[RestaurantContext] Subscription sync failed silently');
  }
};

export const RestaurantProvider = ({ children }: { children: ReactNode }) => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selectedRestaurant, setSelectedRestaurantState] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousCountRef = useRef<number>(0);

  const fetchRestaurants = async (): Promise<Restaurant[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setRestaurants([]);
        setSelectedRestaurantState(null);
        previousCountRef.current = 0;
        return [];
      }

      const { data, error: fetchError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('user_id', sessionData.session.user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const newCount = data?.length || 0;
      setRestaurants(data || []);

      // Sync subscription if restaurant count changed (after initial load)
      if (previousCountRef.current > 0 && newCount !== previousCountRef.current) {
        console.log('[RestaurantContext] Restaurant count changed:', previousCountRef.current, '->', newCount);
        syncSubscriptionPricing();
      }
      previousCountRef.current = newCount;

      // Restore selected restaurant from localStorage
      const savedId = localStorage.getItem(STORAGE_KEY);
      const savedRestaurant = data?.find(r => r.id === savedId);
      
      if (savedRestaurant) {
        setSelectedRestaurantState(savedRestaurant);
      } else if (data && data.length > 0) {
        // Default to first restaurant
        setSelectedRestaurantState(data[0]);
        localStorage.setItem(STORAGE_KEY, data[0].id);
      }
      
      return data || [];
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
      setError(err.message || 'Erreur lors du chargement des restaurants');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const setSelectedRestaurant = (restaurant: Restaurant | null) => {
    setSelectedRestaurantState(restaurant);
    if (restaurant) {
      localStorage.setItem(STORAGE_KEY, restaurant.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  useEffect(() => {
    fetchRestaurants();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        fetchRestaurants();
      } else if (event === 'SIGNED_OUT') {
        setRestaurants([]);
        setSelectedRestaurantState(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <RestaurantContext.Provider
      value={{
        restaurants,
        selectedRestaurant,
        setSelectedRestaurant,
        isLoading,
        error,
        refresh: fetchRestaurants,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurantContext = () => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurantContext must be used within a RestaurantProvider');
  }
  return context;
};
